/**
 * Helpers for the Receipts screen.
 *
 * Kept separate from ReceiptsScreen.tsx so they can be unit-tested
 * independently and reused by future detail / edit views (Phase 5d.4).
 */

import { amountText } from '../../store/currency';

// pos-api stores Entry_Date as a NAIVE wall-clock string in tenant TZ
// (see pos-api/src/utils/wallClock.ts). `new Date(naive)` would re-interpret
// that as host-local — wrong for receipts. We parse the components straight
// from the string so the rendered time is identical to what the ESC/POS
// receipt shows and to what the cashier saw on screen at charge time.
//
// Deliberately NOT anchored at the end. An API build that stamps a trailing
// 'Z' on the wall-clock is still sending wall-clock digits, and the previous
// `$` anchor rejected those strings and fell through to the Date.parse branch
// below — which read them as UTC and shifted every time by the tenant's
// offset. A 07:11 sale in Riyadh rendered as 10:11. Matching the prefix keeps
// the digits authoritative no matter which API version is deployed.
const NAIVE_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,7})?)?/;

interface Parts { year: number; month: number; day: number; hour24: number; minute: number; }

function parseParts(iso: string): Parts | null {
  const m = NAIVE_DATETIME_RE.exec(iso.trim());
  if (m) {
    return {
      year: Number(m[1]), month: Number(m[2]), day: Number(m[3]),
      hour24: Number(m[4]), minute: Number(m[5]),
    };
  }
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  return {
    year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(),
    hour24: d.getHours(), minute: d.getMinutes(),
  };
}

function fmt12(hour24: number, minute: number): string {
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${suffix}`;
}

/** "hh:mm AM/PM" — 12-hour, locale-independent. */
export function formatReceiptTime(iso: string): string {
  const p = parseParts(iso);
  if (!p) return '—';
  return fmt12(p.hour24, p.minute);
}

/** "DD/MM hh:mm AM/PM" — short, locale-neutral. */
export function formatReceiptDateTime(iso: string): string {
  const p = parseParts(iso);
  if (!p) return '—';
  const dd = String(p.day).padStart(2, '0');
  const mo = String(p.month).padStart(2, '0');
  return `${dd}/${mo} ${fmt12(p.hour24, p.minute)}`;
}

/**
 * "DD/MM/YYYY" — the date alone, for the Excel export, where the date and the
 * time of a receipt are two sortable columns rather than one cell.
 */
export function formatReceiptDateOnly(iso: string): string {
  const p = parseParts(iso);
  if (!p) return '—';
  return `${String(p.day).padStart(2, '0')}/${String(p.month).padStart(2, '0')}/${p.year}`;
}

/** "DD/MM/YYYY hh:mm AM/PM" — full date for the Order Details panel. */
export function formatReceiptFullDateTime(iso: string): string {
  const p = parseParts(iso);
  if (!p) return '—';
  const dd = String(p.day).padStart(2, '0');
  const mo = String(p.month).padStart(2, '0');
  return `${dd}/${mo}/${p.year} ${fmt12(p.hour24, p.minute)}`;
}

/** Money at the tenant currency's precision (2 on EGP, 3 on KWD). */
export function formatNetValue(net: number): string {
  return amountText(net);
}

/** Customer cell text. Walk-ins (no customer linked) render as em-dash. */
export function formatCustomerCell(name: string | null, phone: string | null): string {
  if (name && phone) return `${name} · ${phone}`;
  if (name) return name;
  if (phone) return phone;
  return '—';
}

/* ── The filter bar's date+time pickers ─────────────────────────────────
 *
 * The store keeps the date and the time apart, because that is the pair the
 * endpoint takes and because an end can be cleared to "open" while the other
 * keeps its clock. A `datetime-local` input speaks one string, so the two are
 * joined on the way in and split on the way out — the same shape the reports'
 * `splitLocal` uses, kept here so the archive's filter bar does not depend on
 * the reports feature.
 */

/** `('2026-09-16', '14:30')` → `'2026-09-16T14:30'`; no date → `''`. */
export function joinDateTimeLocal(date: string, time: string): string {
  if (!date) return '';
  return `${date}T${/^\d{2}:\d{2}/.test(time) ? time.slice(0, 5) : '00:00'}`;
}

/** `'2026-09-16T14:30'` → its parts; a missing/partial time comes back `''`. */
export function splitDateTimeLocal(value: string): { date: string; time: string } {
  const [date = '', time = ''] = value.split('T');
  return { date, time: /^\d{2}:\d{2}/.test(time) ? time.slice(0, 5) : '' };
}

/**
 * Split a pre-formatted table line ("Sun bed: 5" / "Table: 7") into its
 * tenant terminology term and value so callers can render them as separate
 * label/value cells without duplicating the prefix. The backend always
 * formats the label as "<term>: <value>"; fall back gracefully otherwise.
 */
export function splitTableLabel(label: string): { term: string; value: string } {
  const idx = label.indexOf(':');
  if (idx === -1) return { term: 'Table', value: label.trim() };
  return { term: label.slice(0, idx).trim(), value: label.slice(idx + 1).trim() };
}

/**
 * Compute total page count for pagination controls.
 * Returns 1 when total = 0 (so "Page 1 of 1" is never "Page 1 of 0").
 */
export function computeTotalPages(total: number, limit: number): number {
  if (limit <= 0) return 1;
  if (total <= 0) return 1;
  return Math.ceil(total / limit);
}
